import { apiRequest } from "./apiRequest";
import { getProjectById } from "./storage";

const BASE = process.env.REACT_APP_API_BASE_URL;

export const DEFAULT_THRESHOLD_DAYS = 90;

/**
 * Extract company ID from a project object, handling both nested and flat shapes.
 */
export function getCompanyId(project) {
  return project?.company?.id ?? project?.company_id ?? null;
}

export function normalizeProjectUser(u) {
  return {
    id: u.id,
    name: u.name,
    email_address: u.email_address,
    job_title: u.job_title,
    business_phone: u.business_phone,
    is_whitelisted: u.is_whitelisted,
    last_login_at: u.last_login_at,
    last_activated_at: u.last_activated_at,
    created_at: u.created_at,
    is_active: u.is_active,
    company_permission_template: u.company_permission_template,
    permission_template: u.permission_template,
  };
}

export function withWhitelistDisplay(u) {
  return {
    ...u,
    is_whitelisted: !!u.is_whitelisted,
    is_whitelisted_display: u.is_whitelisted ? "Whitelisted" : "Not Whitelisted",
  };
}

export async function fetchCompaniesAll(ignoreCache = false) {
  const d = await apiRequest(`${BASE}/companies/all/`, {
    method: "GET",
    forceRefresh: ignoreCache,
  });
  return d.companies ?? [];
}

export async function fetchProjectsForCompany(companyId, ignoreCache = false) {
  try {
    const d = await apiRequest(`${BASE}/companies/${companyId}/projects/all/`, {
      method: "GET",
      forceRefresh: ignoreCache,
      suppressErrorToast: true,
    });
    // Check if response has projects array
    if (Array.isArray(d?.projects)) {
      return { status: 200, error: null, company_id: companyId, projects: d.projects };
    }
    return {
      status: d?.status || 500,
      error: d?.error || "Invalid response format",
      company_id: companyId,
    };
  } catch (error) {
    // Handle thrown errors from apiRequest
    return {
      status: error?.status || 500,
      error: error?.message || "Failed to fetch projects",
      company_id: companyId,
    };
  }
}

/**
 * Fetch all projects for all companies.
 */
export async function fetchProjectsAll(ignoreCache = false) {
  const companies = await fetchCompaniesAll(ignoreCache);
  const projectLists = await Promise.all(
    companies.map((c) => fetchProjectsForCompany(c.id, ignoreCache))
  );
  // Return array of wrapper objects (for error handling)
  return projectLists;
}

export async function fetchCompanyUsers(companyId, ignoreCache = false) {
  if (companyId == null) return [];
  const d = await apiRequest(`${BASE}/companies/${companyId}/users/`, {
    method: "GET",
    forceRefresh: ignoreCache,
  });
  return d?.company_users ?? [];
}

export async function fetchUsersForProject(companyId, projectId, ignoreCache = false) {
  if (companyId == null || companyId === "null" || projectId == null) {
    return [];
  }
  const d = await apiRequest(`${BASE}/companies/${companyId}/projects/${projectId}/users/`, {
    method: "GET",
    allow404: true,
    suppressErrorToast: true,
    forceRefresh: ignoreCache,
  });
  if (d === null) return [];
  return (d.project_users || []).map(normalizeProjectUser);
}

// Tiny concurrency limiter: runs mapper across items with a cap.
export async function mapWithConcurrency(items, limit, mapper) {
  const results = new Array(items.length);
  let i = 0;

  const workers = new Array(Math.min(limit, items.length)).fill(0).map(async () => {
    while (i < items.length) {
      const idx = i++;
      try {
        results[idx] = await mapper(items[idx], idx);
      } catch (e) {
        // Store error for potential reporting, but don't fail the entire operation
        results[idx] = { error: e.message || String(e), originalError: e };
      }
    }
  });

  await Promise.all(workers);
  return results;
}

/**
 * Load projects with optional users.
 * Returns wrapper structure: [{ status, error, company_id, projects: [...] }] for consistency
 * @param {Object} options
 * @param {number} [options.projectId] - if provided, only load this single project
 * @param {number} [options.companyId] - required when projectId is set (one API call); when only companyId, load all projects for that company
 * @param {boolean} [options.includeUsers=false] - if true, also attach users per project
 * @param {boolean} [options.ignoreCache=false] - if true, bypass cache
 * @returns {Promise<Array>} array of wrapper objects [{ status, error, company_id, projects: [...] }]
 */
export async function loadProjects({
  projectId = null,
  companyId = null,
  includeUsers = false,
  ignoreCache = false,
} = {}) {
  let foundProject = null;
  let foundCompanyId = null;

  // Case 1: Load single project (caller always passes companyId with projectId)
  if (projectId) {
    const cid = Number(companyId);
    const fromCache = getProjectById(cid, projectId);
    if (fromCache) {
      foundProject = fromCache;
      foundCompanyId = cid;
    }

    if (!foundProject) {
      const result = await fetchProjectsForCompany(cid, ignoreCache);
      if (result.status !== 200 || !Array.isArray(result.projects)) {
        return [result];
      }
      const project = result.projects.find((p) => p.id === projectId);
      if (project) {
        foundProject = project;
        foundCompanyId = cid;
      }
      if (!foundProject) {
        return [
          {
            status: 404,
            error: "Project not found. Project ID: " + projectId,
            company_id: null,
            projects: [],
          },
        ];
      }
    }

    // If users requested, fetch for this single project (guard so we never call with null companyId)
    if (includeUsers && foundCompanyId != null) {
      const users = await fetchUsersForProject(foundCompanyId, projectId, ignoreCache);
      return [
        {
          status: 200,
          error: null,
          company_id: foundCompanyId,
          projects: [{ ...foundProject, users }],
        },
      ];
    }

    return [{ status: 200, error: null, company_id: foundCompanyId, projects: [foundProject] }];
  }

  // Case 2: Load projects for specific company
  if (companyId) {
    const result = await fetchProjectsForCompany(companyId, ignoreCache);
    if (result.status !== 200 || !Array.isArray(result.projects)) {
      return [result]; // Return the error wrapper
    }

    let projects = result.projects;

    // If users requested, fetch for all projects in this company
    if (includeUsers) {
      await mapWithConcurrency(projects, 10, async (p) => {
        try {
          const projectCompanyId = p.company?.id || p.company_id;
          if (!projectCompanyId) {
            p.users = [];
            p.error = "Project missing Company ID";
            return;
          }
          const users = await fetchUsersForProject(projectCompanyId, p.id, ignoreCache);
          p.users = users;
        } catch (error) {
          p.users = [];
          p.error = error.message;
        }
      });
    }
    return [{ status: 200, error: null, company_id: companyId, projects }];
  }

  // Case 3: Load projects for all companies
  const projectLists = await fetchProjectsAll(ignoreCache);
  if (includeUsers) {
    await mapWithConcurrency(projectLists, 10, async (p) => {
      const projectCompanyId = p.company?.id || p.company_id;
      if (!projectCompanyId) {
        p.users = [];
        p.error = "Project missing Company ID";
        return;
      }
      for (const project of p.projects) {
        try {
          const users = await fetchUsersForProject(projectCompanyId, project.id, ignoreCache);
          project.users = users;
        } catch (error) {
          project.users = [];
          project.error = error.message;
        }
      }
    });
  }
  return projectLists;
}
